import { useEffect, useState } from 'react';
import { Check, ClipboardList } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { localeUI } from '../lib/format';

/**
 * NOTAS PARA EL INSPECTOR COMO LISTA (Manuel 2026-10-08): "cuando le dé Save Note, que me salga ahí
 * abajo; si pongo otra, me vuelve a salir otra, como si fuera el update… y coloca quién lo puso y a
 * qué hora, para saber que se envió". Antes era un solo cuadro que se sobreescribía y no decía si se
 * guardó. Cada nota queda con autor y hora; el inspector las ve todas en la app de campo.
 */
export type InspectorNote = { id: string; body: string; author_name: string | null; created_at: string };

const cuando = (iso: string) => new Date(iso).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function InspectorNotesFeed({ claimId, initial, placeholder, mono }: {
  claimId: string;
  /** Si el padre ya tiene la lista (scheduling-info), se pinta sin pedirla de nuevo. */
  initial?: InspectorNote[] | null;
  placeholder?: string;
  mono?: boolean;
}) {
  const { t } = useI18n();
  const [notes, setNotes] = useState<InspectorNote[]>(initial ?? []);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [recien, setRecien] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (initial) { setNotes(initial); return; }
    api.get<{ notes: InspectorNote[] }>(`/api/claims/${claimId}/inspector-notes`).then((r) => setNotes(r.notes)).catch(() => { /* la lista queda vacía; el error sale al guardar */ });
  }, [claimId, initial]);

  async function guardar() {
    const body = text.trim();
    if (!body) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ note: InspectorNote; notes: InspectorNote[] }>(`/api/claims/${claimId}/inspector-notes`, { text: body });
      setNotes(r.notes); setText(''); setRecien(r.note.id);
      window.setTimeout(() => setRecien(null), 2500);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <textarea className="input" style={{ width: '100%', minHeight: 64, fontFamily: mono ? 'var(--font-mono)' : undefined }} value={text}
        onChange={(e) => setText(e.target.value)} placeholder={placeholder ?? t('cr.inspectorNotesPh')}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void guardar(); } }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} disabled={busy || !text.trim()} onClick={() => void guardar()}>
          {busy ? '…' : t('cr.saveNotes')}
        </button>
        {recien && <span style={{ color: 'var(--green)', fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cr.noteSent')}</span>}
        {err && <span style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</span>}
      </div>
      {/* LA LISTA, como los updates: la más nueva arriba, cada una con quién y a qué hora. */}
      {notes.length === 0 ? (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('cr.noNotesYet')}</div>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {notes.map((n) => (
            <li key={n.id} style={{ borderLeft: `3px solid ${recien === n.id ? 'var(--green)' : 'var(--line)'}`, background: 'var(--surface-2)', borderRadius: 8, padding: '7px 10px' }}>
              <div style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.5, whiteSpace: 'pre-wrap', userSelect: 'text' }}>{n.body}</div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 3 }}>
                <ClipboardList size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> <b>{n.author_name || t('cr.noteByOffice')}</b> · {cuando(n.created_at)} · {t('cr.noteSentShort')}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
